// Plain-language labelling for user-facing copy (Guide #2 remediation).
//
// The product must never leak raw canonical field names (e.g. `obligatedUsdM`),
// raw status/type enums (e.g. `pending_human_review`, `review_for_consolidation`),
// raw connector ids (e.g. `CONN-PMRT-FINANCE`), or dev-speak placeholders
// (`-- missing --`, `severity n/a`) into text a human reads. This module is the
// single web-side source of truth for turning those tokens into friendly copy.
//
// House pattern where a data-steward audience benefits: render the friendly
// label as the primary (often bold) text and keep the canonical token as a
// small mono hint or a `title`/tooltip attribute for traceability.
//
// The connector map here mirrors the API's `sourceSystemLabel`
// (apps/api/src/services/owhaFactory.service.js) so web and server agree on the
// same friendly names for the known connectors.

// Placeholder shown when a value is genuinely absent.
export const MISSING_VALUE_LABEL = 'Not provided';

// Canonical field key -> friendly, human-readable label. These labels are the
// same friendly names the API validation messages use (per the R1/R2 contract),
// e.g. `obligatedUsdM` -> "Obligated $M" so a message reads
// "Obligated $M must be a number before commit.".
export const FIELD_LABELS = {
  // Financial execution figures.
  obligatedUsdM: 'Obligated $M',
  plannedBudgetUsdM: 'Planned budget $M',
  actualSpendUsdM: 'Actual spend $M',
  expendedUsdM: 'Expended $M',
  forecastAtCompletionUsdM: 'Forecast at completion $M',
  forecastVarianceUsdM: 'Forecast variance $M',
  // Identity / scope.
  scopeId: 'Project',
  projectId: 'Project',
  programId: 'Program',
  portfolioId: 'Portfolio',
  // Time / period.
  fiscalYear: 'Fiscal year',
  period: 'Period',
  accountingPeriod: 'Accounting period',
  asOfDate: 'As-of date',
  sourceUpdatedAt: 'Source updated',
  dueDate: 'Due date',
  // Provenance / ownership.
  sourceRecordId: 'Source record',
  sourceSystem: 'Source system',
  assignmentGroup: 'Assignment group',
  ownerUserId: 'Owner',
  title: 'Title',
  reviewState: 'Review state',
  severity: 'Severity',
};

// Enum values whose generic sentence-case would read wrong or where product
// copy intentionally differs. Everything not listed falls through to the
// generic snake/camel/kebab -> sentence-case transform below.
const ENUM_LABEL_OVERRIDES = {
  // AI governed-run review states.
  pending_human_review: 'Pending human review',
  human_reviewed: 'Human reviewed',
  approved_for_export: 'Approved for export',
  auto_approved: 'Auto approved',
  needs_rework: 'Needs rework',
  rejected: 'Rejected',
  // AI consolidation group recommendation.
  review_for_consolidation: 'Review for consolidation',
  // Reconciliation case statuses.
  'needs-data-steward-decision': 'Needs data steward decision',
};

// Known acronyms that should keep their casing in generic sentence-case output.
const ACRONYMS = new Set([
  'ai',
  'id',
  'loe',
  'qa',
  'evm',
  'cpi',
  'spi',
  'usd',
  'kpi',
  'rapid',
  // 'ux' was missing, so feedback categories like `ux_confusion` rendered as the
  // wrong-cased "Ux Confusion". Registering it as an acronym yields "UX confusion".
  'ux',
  'uat',
  'cui',
]);

// Generic transform: split camelCase / snake_case / kebab-case into words, then
// sentence-case (first word capitalized, rest lower) while preserving known
// acronyms.
function toSentenceCase(value) {
  const spaced = String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return '';
  const words = spaced.split(' ').map((word, index) => {
    const lower = word.toLowerCase();
    if (ACRONYMS.has(lower)) return word.toUpperCase();
    if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    return lower;
  });
  return words.join(' ');
}

/**
 * Friendly label for a canonical field key.
 * Falls back to sentence-case for keys not in FIELD_LABELS.
 * @param {string} field
 * @returns {string}
 */
export function fieldLabel(field) {
  if (field == null || field === '') return '';
  if (Object.prototype.hasOwnProperty.call(FIELD_LABELS, field)) return FIELD_LABELS[field];
  return toSentenceCase(field) || String(field);
}

/**
 * Friendly label for a status/type/reason-code enum value.
 * Uses ENUM_LABEL_OVERRIDES first, then a generic sentence-case transform.
 * @param {string} value
 * @param {string} [fallback] label to use when value is null/blank
 * @returns {string}
 */
export function humanizeEnum(value, fallback = '') {
  if (value == null || value === '') return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;
  if (Object.prototype.hasOwnProperty.call(ENUM_LABEL_OVERRIDES, raw)) {
    return ENUM_LABEL_OVERRIDES[raw];
  }
  const lower = raw.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(ENUM_LABEL_OVERRIDES, lower)) {
    return ENUM_LABEL_OVERRIDES[lower];
  }
  return toSentenceCase(raw) || raw;
}

// Ingestion target entity types -> friendly labels.
const ENTITY_TYPE_LABELS = {
  financial_record: 'Financial record',
  action: 'Action',
  work_item: 'Work item',
};

/**
 * Friendly label for an ingestion target entity type.
 * @param {string} value
 * @returns {string}
 */
export function entityTypeLabel(value) {
  if (value == null || value === '') return '';
  if (Object.prototype.hasOwnProperty.call(ENTITY_TYPE_LABELS, value)) {
    return ENTITY_TYPE_LABELS[value];
  }
  return humanizeEnum(value);
}

/**
 * Friendly name for an AI agent id (e.g. "AGT-PORTFOLIO-RISK" -> "Portfolio
 * Risk agent"). Non-`AGT-` values pass through unchanged. Returns '' for empty.
 * @param {string} value
 * @returns {string}
 */
export function agentLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^agt-/i.test(raw)) {
    const titled = raw
      .replace(/^agt-/i, '')
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
    return titled ? `${titled} agent` : raw;
  }
  return raw;
}

// Known acronyms that should stay upper-cased in a humanized role label.
const ROLE_LABEL_ACRONYMS = new Set(['AI', 'SME', 'IMO', 'RBAC', 'CUI', 'PII', 'PHI']);

/**
 * Friendly name for a role id (e.g. "ROLE-PORTFOLIO-MANAGER" -> "Portfolio
 * Manager", "ROLE-CLINICAL-SME-REVIEWER" -> "Clinical SME Reviewer"). A value
 * that is not a `ROLE-` id (e.g. an already-humanized "Portfolio Manager" or a
 * seeded persona label) passes through unchanged. Returns '' for empty. Keeps
 * raw role enums out of user-facing copy per the plain-language standard.
 * @param {string} value
 * @returns {string}
 */
export function roleLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!/^role-/i.test(raw)) return raw;
  const titled = raw
    .replace(/^role-/i, '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => {
      const upper = part.toUpperCase();
      if (ROLE_LABEL_ACRONYMS.has(upper)) return upper;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
  return titled || raw;
}

// Connector id -> friendly source-system name. Mirrors the API's
// SOURCE_SYSTEM_LABELS so a figure sourced from `CONN-PMRT-FINANCE` reads the
// same ("FMIS / Tableau") on both tiers. `ManualSeed` is additionally softened
// to "Manually entered" for trust panels (a web-only friendliness beyond the API).
const SOURCE_SYSTEM_LABELS = {
  'CONN-PMRT-FINANCE': 'FMIS / Tableau',
  'CONN-SERVICENOW-CHANGE': 'ServiceNow',
  // The provenance-hero source lane routes CONN-SERVICENOW-CMDB through here. An
  // explicit entry gives it the canonical brand casing "ServiceNow CMDB" instead
  // of the generic title-case fallback "Servicenow Cmdb". The bare underscore
  // form a lane key may carry is normalized to the same label.
  'CONN-SERVICENOW-CMDB': 'ServiceNow CMDB',
  SERVICENOW_CMDB: 'ServiceNow CMDB',
  'CONN-JIRA-AGILE': 'Jira',
  MANUALSEED: 'Manually entered',
  // Bare product names must not be camelCase-split ("ServiceNow", not "Service Now").
  SERVICENOW: 'ServiceNow',
  JIRA: 'Jira',
};

/**
 * Friendly source-system name for a connector id or governed-seed source.
 * - Known connectors -> curated label.
 * - Other `CONN-*` ids -> strip the prefix and title-case the remainder.
 * - Non-connector values (e.g. "PRIME", "PMRT") -> re-spaced/capitalized, never
 *   a bare camelCase token.
 * Returns `null` for empty input (callers decide the empty fallback).
 * @param {string} value
 * @returns {string|null}
 */
export function sourceSystemLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const known = SOURCE_SYSTEM_LABELS[raw.toUpperCase()];
  if (known) return known;
  if (/^conn-/i.test(raw)) {
    const titled = raw
      .replace(/^conn-/i, '')
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
    // A bare "CONN-" with nothing after must still never render raw.
    return titled || raw;
  }
  // Non-connector: re-space camelCase/kebab/snake and capitalize the first
  // character only (preserve existing casing of the rest, e.g. "PMRT").
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}
